/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { CommonModule } from '@angular/common';
import { Component, computed, input, Signal } from '@angular/core';
import { cva, VariantProps } from 'class-variance-authority';
import { IconComponent, IconConfig, IconVariants } from '../../icon/icon.component';
import { FileButtonIconName } from '../../icon/icons';
import { SpinnerIconComponent } from '../../icons/spinner-icon/spinner-icon.component';

export const uploadButtonVariants = cva(
  [
    'z-10 inline-flex grow items-center justify-start gap-4 wrap-break-word rounded-md text-primary cursor-pointer peer-disabled:cursor-not-allowed',
    'hover:bg-ghost-hover',
    'peer-focus-visible:outline-solid peer-focus-visible:outline-2 peer-focus-visible:outline-focus peer-focus-visible:bg-ghost-hover peer-focus-visible:outline-offset-2 peer-focus-visible:border-background-200',
  ],
  {
    variants: {
      variant: {
        label: 'border border-transparent py-3 px-6 bg-background-50 w-full',
        icon: 'border border-transparent p-2 w-fit',
        'label-outline': 'border border-primary py-2 px-4 min-w-32 w-fit bg-background-50 shadow-md ',
      },
    },
    defaultVariants: {
      variant: 'label',
    },
  },
);
export type UploadButtonVariants = VariantProps<typeof uploadButtonVariants>;

@Component({
  selector: 'ods-file-upload-button',
  imports: [CommonModule, IconComponent, SpinnerIconComponent],
  host: {
    class: 'relative',
  },
  template: `
    <input
      type="file"
      [id]="id()"
      class="peer absolute top-0 left-0 z-0 h-0 w-0 opacity-0"
      [accept]="accept()"
      (click)="resetInput($event)"
      [disabled]="isLoading()"
      [multiple]="multi()"
      [attr.data-test-id]="dataTestId()"
      [attr.aria-required]="isRequired()"
      [attr.aria-label]="ariaLabel()"
    />
    <label [for]="id()" [ngClass]="uploadButtonVariants({ variant: variant() })">
      @if (isLoading()) {
        <ods-spinner-icon [size]="spinnerSize()" data-test-class="spinner" />
      } @else if (icon(); as iconName) {
        <ods-icon [name]="iconName" [config]="iconConfigWithLabel()" data-test-class="icon" />
      }
      @if (text()) {
        <p class="text-primary grow text-center">
          {{ text() }}
          @if (isRequired()) {
            <i aria-hidden="true">*</i>
          }
        </p>
      }
    </label>
  `,
})
export class FileUploadButtonComponent {
  public readonly id = input.required<string>();
  public readonly ariaLabel = input<string | null>(null);
  public readonly isLoading = input(false);
  public readonly spinnerSize = input<IconVariants['size']>('medium');
  public readonly accept = input<string>('*/*');
  public readonly multi = input(false);
  public readonly variant = input<UploadButtonVariants['variant']>();
  public readonly dataTestId = input.required<string>();
  public readonly isRequired = input(false);
  public readonly icon = input<FileButtonIconName>();
  public readonly iconConfig = input<IconConfig>();
  public readonly text = input<string>('');

  public readonly uploadButtonVariants = uploadButtonVariants;

  public readonly iconConfigWithLabel: Signal<IconConfig> = computed(() => ({
    ...this.iconConfig(),
    label: this.ariaLabel(),
  }));

  public resetInput(event: Event): void {
    (event.target as HTMLInputElement).value = '';
  }
}
